import { create } from 'zustand'
import type { RAGSource, DisplaySource } from '@/types/source'
import type { ReplyCut } from '@/services/ragConnector'
import type { ReplyWorkflow } from '@/utils/workflowReplyText'

/**
 * @deprecated Use RAGSource or DisplaySource from @/types/source instead
 */
export interface Source {
  id: string
  title: string
  content: string
  score: number
  metadata?: Record<string, unknown>
}

// Re-export source types for backward compatibility
export type { RAGSource, DisplaySource }

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
  /** RAG sources associated with this message (for assistant messages) */
  sources?: RAGSource[]
  isStreaming?: boolean
  error?: string
  /** The reply was cut short (SA-1241) */
  cut?: ReplyCut
  /** A workflow, not the persona, answered (SA-474); shown live, never synced */
  workflow?: ReplyWorkflow
  /** Attachment fields (chat_attachments feature) */
  mediaType?: 'image' | 'document'
  mediaUrl?: string
  attachment?: { filename: string; mimeType: string; byteSize?: number }
}

export interface Conversation {
  id: string
  personaId: string | null
  title: string
  messages: Message[]
  createdAt: number
  updatedAt: number
}

export interface ConversationSummary {
  id: string
  title: string
  personaId: string | null
  personaName: string | null
  channel: string
  messageCount: number
  createdAt: number
  updatedAt: number
}

interface ChatState {
  // Current conversation
  currentConversationId: string | null
  conversations: Record<string, Conversation>
  conversationList: ConversationSummary[]

  // Loading/sync state
  isLoading: boolean
  isSyncing: boolean
  error: string | null

  // Streaming state
  isStreaming: boolean
  streamingMessageId: string | null

  // State setters
  setConversationList: (list: ConversationSummary[]) => void
  setConversation: (conversation: Conversation) => void
  setConversations: (conversations: Conversation[]) => void
  setLoading: (loading: boolean) => void
  setSyncing: (syncing: boolean) => void
  setError: (error: string | null) => void

  // Conversation actions (local state only - API calls in hook)
  createConversation: (personaId: string | null, id?: string) => string
  setCurrentConversation: (id: string | null) => void
  addMessage: (conversationId: string, message: Omit<Message, 'id' | 'timestamp'>, id?: string) => string
  updateMessage: (conversationId: string, messageId: string, updates: Partial<Message>) => void
  removeMessage: (conversationId: string, messageId: string) => void
  appendToMessage: (conversationId: string, messageId: string, content: string) => void
  updateConversationTitle: (conversationId: string, title: string) => void
  deleteConversation: (id: string) => void
  clearConversations: () => void

  // Streaming actions
  setStreaming: (isStreaming: boolean, messageId?: string | null) => void
}

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

export const useChatStore = create<ChatState>()((set) => ({
  currentConversationId: null,
  conversations: {},
  conversationList: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  isStreaming: false,
  streamingMessageId: null,

  setConversationList: (list) => set({ conversationList: list }),

  setConversation: (conversation) => set((state) => ({
    conversations: {
      ...state.conversations,
      [conversation.id]: conversation,
    },
  })),

  setConversations: (conversations) => {
    const conversationsMap: Record<string, Conversation> = {}
    conversations.forEach((c) => {
      conversationsMap[c.id] = c
    })
    set({ conversations: conversationsMap })
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setSyncing: (syncing) => set({ isSyncing: syncing }),
  setError: (error) => set({ error }),

  createConversation: (personaId, existingId) => {
    const id = existingId || generateId()
    const conversation: Conversation = {
      id,
      personaId,
      title: 'New Conversation',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }

    set((state) => ({
      conversations: { ...state.conversations, [id]: conversation },
      currentConversationId: id,
    }))

    return id
  },

  setCurrentConversation: (id) => set({ currentConversationId: id }),

  addMessage: (conversationId, message, existingId) => {
    const id = existingId || generateId()
    const fullMessage: Message = {
      ...message,
      id,
      timestamp: Date.now(),
    }

    set((state) => {
      const conversation = state.conversations[conversationId]
      if (!conversation) return state

      // Auto-name from first user message
      let title = conversation.title
      const isAutoNaming = message.role === 'user' && conversation.messages.length === 0
      if (isAutoNaming) {
        const titleSource = message.content || message.attachment?.filename || ''
        if (titleSource) {
          title = titleSource.slice(0, 50) + (titleSource.length > 50 ? '...' : '')
        }
      }

      // Update conversationList too (sidebar) when auto-naming
      const updatedList = isAutoNaming
        ? state.conversationList.map((c) =>
            c.id === conversationId ? { ...c, title, updatedAt: Date.now(), messageCount: c.messageCount + 1 } : c
          )
        : state.conversationList

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            title,
            messages: [...conversation.messages, fullMessage],
            updatedAt: Date.now(),
          },
        },
        conversationList: updatedList,
      }
    })

    return id
  },

  updateMessage: (conversationId, messageId, updates) => {
    set((state) => {
      const conversation = state.conversations[conversationId]
      if (!conversation) return state

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: conversation.messages.map((msg) =>
              msg.id === messageId ? { ...msg, ...updates } : msg
            ),
            updatedAt: Date.now(),
          },
        },
      }
    })
  },

  // Used when an optimistic message never made it to the backend (e.g. a
  // failed attachment upload) — the send is retried from a clean slate.
  removeMessage: (conversationId, messageId) => {
    set((state) => {
      const conversation = state.conversations[conversationId]
      if (!conversation) return state

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: conversation.messages.filter((msg) => msg.id !== messageId),
            updatedAt: Date.now(),
          },
        },
      }
    })
  },

  appendToMessage: (conversationId, messageId, content) => {
    set((state) => {
      const conversation = state.conversations[conversationId]
      if (!conversation) return state

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: conversation.messages.map((msg) =>
              msg.id === messageId ? { ...msg, content: msg.content + content } : msg
            ),
            updatedAt: Date.now(),
          },
        },
      }
    })
  },

  updateConversationTitle: (conversationId, title) => {
    set((state) => {
      const conversation = state.conversations[conversationId]

      // Always update the conversation list (sidebar)
      const updatedList = state.conversationList.map((c) =>
        c.id === conversationId ? { ...c, title, updatedAt: Date.now() } : c
      )

      // Also update full conversation data if loaded
      if (conversation) {
        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conversation,
              title,
              updatedAt: Date.now(),
            },
          },
          conversationList: updatedList,
        }
      }

      // Only update the list if full conversation not loaded
      return {
        conversationList: updatedList,
      }
    })
  },

  deleteConversation: (id) => {
    set((state) => {
      const { [id]: _, ...rest } = state.conversations
      return {
        conversations: rest,
        conversationList: state.conversationList.filter((c) => c.id !== id),
        currentConversationId:
          state.currentConversationId === id ? null : state.currentConversationId,
      }
    })
  },

  clearConversations: () => {
    set({ conversations: {}, conversationList: [], currentConversationId: null })
  },

  setStreaming: (isStreaming, messageId = null) => {
    set({ isStreaming, streamingMessageId: messageId })
  },
}))
